import { assign, createActor, setup, type ActorRefFrom } from 'xstate';

import { warnUnhandledEvent } from '@core/utils/xstate-utils.js';

import { setSentryUser } from './sentry.js';

type SentryContext = { unsubProfile: (() => void) | null };

type SentryEvent =
  | { type: 'START_PROFILE_SUBSCRIPTION'; unsub: () => void }
  | { type: 'PROFILE_SET'; userId: string }
  | { type: 'PROFILE_CLEAR' };

/**
 * Sentry user-context lifecycle as an xstate v5 machine.
 *
 * Two states (`noUser`, `withUser`). The Sentry tag's `onSet` starts the
 * profile subscription (necessary-tier — no consent gate) and forwards
 * `PROFILE_SET` / `PROFILE_CLEAR` events into the machine. The machine
 * `setSentryUser(...)` on entry to the corresponding state.
 *
 * Tracking analytics events does NOT pass through this machine — Sentry's
 * `addBreadcrumb` is in-process and always ready, so the Sentry tag's
 * `onTrack` fires breadcrumbs directly.
 */
export const sentryMachine = setup({
  types: {
    context: {} as SentryContext,
    events: {} as SentryEvent,
  },
  actions: {
    setUserFromEvent: (_, params: { userId: string }) => setSentryUser(params.userId),
    clearUser: () => setSentryUser(null),
    storeUnsub: assign({
      unsubProfile: (_, params: { unsub: () => void }) => params.unsub,
    }),
  },
}).createMachine({
  id: 'sentry',
  initial: 'noUser',
  context: { unsubProfile: null },
  on: {
    '*': {
      actions: ({ self, event }) => warnUnhandledEvent(self.id, event.type),
    },
    START_PROFILE_SUBSCRIPTION: {
      actions: {
        type: 'storeUnsub',
        params: ({ event }) => ({ unsub: event.unsub }),
      },
    },
  },
  states: {
    noUser: {
      on: {
        PROFILE_SET: {
          target: 'withUser',
          actions: {
            type: 'setUserFromEvent',
            params: ({ event }) => ({ userId: event.userId }),
          },
        },
        // The profile subscription emits the current state on subscribe; when
        // the me-query hasn't loaded yet that's `userId=null`, so PROFILE_CLEAR
        // arrives while we're already in the noUser state. No-op.
        PROFILE_CLEAR: {},
      },
    },
    withUser: {
      on: {
        PROFILE_CLEAR: { target: 'noUser', actions: ['clearUser'] },
        PROFILE_SET: {
          target: 'withUser',
          actions: {
            type: 'setUserFromEvent',
            params: ({ event }) => ({ userId: event.userId }),
          },
        },
      },
    },
  },
});

let actor = createActor(sentryMachine, { id: 'sentry' });
actor.start();

export function sentryActorSend(event: SentryEvent): void {
  actor.send(event);
}

export function getSentryActor(): ActorRefFrom<typeof sentryMachine> {
  return actor;
}

/** Test-only: stop the running actor and create a fresh one. */
export function __resetSentryMachineForTests(): void {
  actor.stop();
  actor = createActor(sentryMachine, { id: 'sentry' });
  actor.start();
}
